import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronLeft } from "lucide-react"
import { openPlusPortal } from "~/api/plus"
import { useProfile } from "~/hooks/useProfile"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"

function friendlyStatus(subscription: {
  status: string
  complimentary?: boolean
  access_active: boolean
}): string {
  if (subscription.complimentary || subscription.status === "complimentary") {
    return "Complimentary Doggy Dynamo Plus"
  }
  switch (subscription.status) {
    case "active":
      return "Active"
    case "cancelled":
      return "Cancelling"
    case "grace_period":
      return "Payment Issue"
    case "expired":
    case "refunded":
      return "Expired"
    default:
      return subscription.access_active ? "Active" : "Expired"
  }
}

export function SubscriptionPage() {
  const navigate = useNavigate()
  const { profile } = useProfile()
  const [portalError, setPortalError] = useState<string | null>(null)
  const [portalBusy, setPortalBusy] = useState(false)

  const subscription = profile?.subscription
  const isComplimentary =
    Boolean(subscription?.complimentary) ||
    (subscription?.provider === "admin" && subscription?.status === "complimentary")
  const canManageBilling =
    Boolean(subscription?.stripe_customer_present) && subscription?.provider === "stripe"

  async function handleManage() {
    setPortalError(null)
    setPortalBusy(true)
    try {
      const result = await openPlusPortal()
      if (result.url) {
        window.location.href = result.url
        return
      }
      setPortalError(result.message || "Billing portal is unavailable.")
    } catch (err) {
      setPortalError(err instanceof Error ? err.message : "Could not open billing portal")
    } finally {
      setPortalBusy(false)
    }
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
      <header className="mb-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/profile")}
            aria-label="Back to Profile"
            className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronLeft size={20} aria-hidden />
          </button>
          <h1 className="flex-1 text-lg font-semibold text-[var(--color-text-primary)]">
            Subscription
          </h1>
      </header>

      <div className="space-y-4">
        {subscription?.access_active ? (
          <>
            <div className="rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4">
              <h2 className="mb-3 font-medium text-[var(--color-text-primary)]">Current Plan</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-[var(--color-text-secondary)]">Status</span>
                  <span className="text-right font-medium text-[var(--color-text-primary)]">
                    {friendlyStatus(subscription)}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-[var(--color-text-secondary)]">Plan</span>
                  <span className="font-medium text-[var(--color-text-primary)]">Doggy Dynamo Plus</span>
                </div>
                {isComplimentary ? (
                  <p className="pt-2 text-[var(--color-text-secondary)]">
                    Your membership is active and no payment is currently required.
                    {subscription.current_period_ends_at
                      ? ` Access continues until ${new Date(subscription.current_period_ends_at).toLocaleDateString()}.`
                      : " This complimentary access does not expire."}
                  </p>
                ) : (
                  <>
                    {subscription.billing_interval && (
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-secondary)]">Billing</span>
                        <span className="font-medium capitalize text-[var(--color-text-primary)]">
                          {subscription.billing_interval}
                        </span>
                      </div>
                    )}
                    {subscription.current_period_ends_at && (
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-secondary)]">Renews / ends</span>
                        <span className="font-medium text-[var(--color-text-primary)]">
                          {new Date(subscription.current_period_ends_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-secondary)]">Provider</span>
                      <span className="font-medium capitalize text-[var(--color-text-primary)]">
                        {subscription.provider === "stripe" ? "Stripe" : subscription.provider}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {canManageBilling && (
              <button
                type="button"
                disabled={portalBusy}
                onClick={() => void handleManage()}
                className="w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-4 py-3 text-center font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
              >
                {portalBusy ? "Opening…" : "Manage Subscription"}
              </button>
            )}
            {portalError && (
              <p className="text-sm text-red-600" role="alert">
                {portalError}
              </p>
            )}
          </>
        ) : (
          <div className="rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-6 text-center">
            <p className="mb-4 text-[var(--color-text-primary)]">No active subscription</p>
            <button
              type="button"
              onClick={() => navigate("/plus")}
              className="rounded-lg bg-[var(--color-brand-primary)] px-6 py-2 font-medium text-white hover:opacity-90"
            >
              Explore Plus
            </button>
          </div>
        )}
      </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
